import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SuccessPopup from "./SuccessPopup";
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  Youtube, 
  Mail, 
  Phone, 
  MapPin,
  Send,
  ArrowRight,
  Heart
} from "lucide-react";
import { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";

const Footer = () => {
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      // Use PHP backend - localhost development
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin;
      
      console.log('🚀 Sending to PHP API:', `${API_BASE}/php/newsletter.php`);
      
      const res = await fetch(`${API_BASE}/php/newsletter.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Request failed');
      setEmail("");
      setShowSuccessPopup(true);
    } catch (err) {
      console.error('Newsletter subscription failed', err);
      alert(err?.message || 'Sorry, failed to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com", color: "hover:text-blue-400" },
    { icon: Twitter, href: "https://twitter.com", color: "hover:text-blue-300" },
    { icon: Linkedin, href: "https://linkedin.com", color: "hover:text-blue-500" },
    { icon: Youtube, href: "https://youtube.com", color: "hover:text-red-500" },
  ];

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Courses", path: "/courses" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const resourceLinks = [
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms of Service", path: "/terms-of-service" },
  ];

  const additionalResources = [
    { name: "FAQs", path: "/faq" },
    { name: "Blog", path: "/blog" },
    { name: "Learning Resources", path: "/learning-resources" },
  ];

  return (
    <>
      <footer className={`relative overflow-hidden transition-all duration-500 ${
        isDarkMode 
          ? 'text-white bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900' 
          : 'text-white bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900'
      }`}>
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full blur-3xl animate-pulse bg-blue-500/10"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl delay-1000 animate-pulse bg-purple-500/10"></div>
      </div>
      
      <div className="relative z-10">
          {/* Main content */}
          <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 sm:py-8">
            {/* Mobile: Compact Layout */}
            <div className="block sm:hidden">
              <div className="space-y-6">
                
                {/* Brand Section - Mobile */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="text-center"
                >
                  <motion.h2 
                    className="mb-3 text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    Skill Earth Academy
                  </motion.h2>
                  <p className={`leading-relaxed mb-4 text-sm font-medium transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-200'
                  }`}>
                    Empowering learners through hands-on technology education.
                  </p>
                  
                  {/* Newsletter - Mobile */}
                  <div className="space-y-2">
                    <h4 className={`text-base font-bold transition-colors duration-500 ${
                      isDarkMode ? 'text-white' : 'text-white'
                    }`}>Stay Updated</h4>
                    <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email"
                        className="flex-1 px-3 py-2 text-sm font-medium placeholder-gray-400 text-white rounded-lg border transition-all duration-300 bg-white/10 border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                        required
                      />
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-3 py-2 font-semibold bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg transition-all duration-300 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Send size={14} />
                      </motion.button>
                    </form>
                  </div>
                </motion.div>

                {/* Quick Links & Resources - Side by Side */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                  className="grid grid-cols-2 gap-4"
                >
                  {/* Quick Links */}
                  <div>
                    <h3 className={`text-base font-bold mb-3 transition-colors duration-500 ${
                      isDarkMode ? 'text-white' : 'text-white'
                    }`}>Quick Links</h3>
                    <ul className="space-y-2">
                      {quickLinks.map((link, index) => (
                        <motion.li
                          key={link.name}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                        >
                          <Link 
                            to={link.path} 
                            className={`transition-all duration-300 flex items-center group text-sm font-medium ${
                              isDarkMode 
                                ? 'text-gray-300 hover:text-white' 
                                : 'text-gray-200 hover:text-white'
                            }`}
                          >
                            <motion.div
                              className="mr-2 w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100"
                              whileHover={{ scale: 1.5 }}
                            />
                            {link.name}
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Resources */}
                  <div>
                    <h3 className={`text-base font-bold mb-3 transition-colors duration-500 ${
                      isDarkMode ? 'text-white' : 'text-white'
                    }`}>Resources</h3>
                    <ul className="space-y-2">
                      {additionalResources.map((link, index) => (
                        <motion.li
                          key={link.name}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                        >
                          <Link 
                            to={link.path} 
                            className={`transition-all duration-300 flex items-center group text-sm font-medium ${
                              isDarkMode 
                                ? 'text-gray-300 hover:text-white' 
                                : 'text-gray-200 hover:text-white'
                            }`}
                          >
                            <motion.div
                              className="mr-2 w-1 h-1 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100"
                              whileHover={{ scale: 1.5 }}
                            />
                            {link.name}
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                {/* Contact & Social - Mobile */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                  className="text-center"
                >
                  <h3 className={`text-base font-bold mb-3 transition-colors duration-500 ${
                    isDarkMode ? 'text-white' : 'text-white'
                  }`}>Get in Touch</h3>
                  
                  {/* Contact Info - Compact */}
                  <div className="mb-4 space-y-2">
                    <motion.div 
                      className="flex gap-2 justify-center items-start"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MapPin size={14} className="flex-shrink-0 mt-0.5 text-blue-400" />
                      <p className={`text-sm font-semibold leading-relaxed transition-colors duration-500 ${
                        isDarkMode ? 'text-white' : 'text-white'
                      }`}>
                        C-116/2, Gali no 19, Molarband extension, Badarpur, New Delhi - 110044, Landmark: Kamal medical
                      </p>
                    </motion.div>
                    
                    <motion.div 
                      className="flex gap-2 justify-center items-center"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Mail size={14} className="flex-shrink-0 text-blue-400" />
                      <a 
                        href="mailto:support@skillsearthacademy.com" 
                        className={`text-sm font-semibold transition-colors duration-300 ${
                          isDarkMode 
                            ? 'text-white hover:text-blue-300' 
                            : 'text-white hover:text-blue-200'
                        }`}
                      >
                        support@skillsearthacademy.com
                      </a>
                    </motion.div>
                    
                    <motion.div 
                      className="flex gap-2 justify-center items-center"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Phone size={14} className="flex-shrink-0 text-blue-400" />
                      <div className="text-sm font-semibold">
                        <a href="tel:+917827153697" className={`transition-colors duration-300 block ${
                          isDarkMode ? 'text-white hover:text-blue-300' : 'text-white hover:text-blue-200'
                        }`}>
                          +91 78271 53697
                        </a>
                        <a href="tel:+918586084775" className={`transition-colors duration-300 block ${
                          isDarkMode ? 'text-white hover:text-blue-300' : 'text-white hover:text-blue-200'
                        }`}>
                          +91 8586084775
                        </a>
                      </div>
                    </motion.div>
                  </div>

                  {/* Social Links - Mobile */}
                  <div className="flex justify-center space-x-3">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-3 rounded-lg transition-all duration-300 backdrop-blur-sm ${
                          isDarkMode 
                            ? 'text-white border bg-white/20 hover:bg-white/30 hover:text-blue-300 border-white/20 hover:border-white/40' 
                            : 'text-white border bg-white/30 hover:bg-white/40 hover:text-blue-200 border-white/30 hover:border-white/50'
                        }`}
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                      >
                        <social.icon size={20} className="text-white" />
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Desktop: Original Layout */}
            <div className="hidden sm:block">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              
              {/* Brand Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-1"
            >
              <motion.h2 
                  className="mb-4 text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
              >
                Skill Earth Academy
              </motion.h2>
                <p className={`leading-relaxed mb-6 text-sm font-medium transition-colors duration-500 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-200'
                }`}>
                Empowering learners through hands-on technology education. Learn skills that matter in the real world.
              </p>
              
                {/* Newsletter */}
                <div className="space-y-3">
                  <h4 className={`text-base font-bold transition-colors duration-500 ${
                  isDarkMode ? 'text-white' : 'text-white'
                }`}>Stay Updated</h4>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email"
                      className="flex-1 px-3 py-2 text-sm font-medium placeholder-gray-400 text-white rounded-lg border transition-all duration-300 bg-white/10 border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                    required
                  />
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                      className="px-4 py-2 font-semibold bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg transition-all duration-300 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Send size={16} />
                  </motion.button>
                </form>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            >
                <h3 className={`text-lg font-bold mb-6 transition-colors duration-500 ${
                  isDarkMode ? 'text-white' : 'text-white'
                }`}>Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  >
                    <Link 
                      to={link.path} 
                        className={`transition-all duration-300 flex items-center group text-base font-medium ${
                          isDarkMode 
                            ? 'text-gray-300 hover:text-white' 
                            : 'text-gray-200 hover:text-white'
                        }`}
                    >
                        <motion.div
                          className="mr-3 w-1 h-1 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100"
                          whileHover={{ scale: 1.5 }}
                        />
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

              {/* Resources Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
                <h3 className={`text-lg font-bold mb-6 transition-colors duration-500 ${
                  isDarkMode ? 'text-white' : 'text-white'
                }`}>Resources</h3>
              <ul className="space-y-3">
                  {additionalResources.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  >
                    <Link 
                      to={link.path} 
                        className={`transition-all duration-300 flex items-center group text-base font-medium ${
                          isDarkMode 
                            ? 'text-gray-300 hover:text-white' 
                            : 'text-gray-200 hover:text-white'
                        }`}
                    >
                        <motion.div
                          className="mr-3 w-1 h-1 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100"
                          whileHover={{ scale: 1.5 }}
                        />
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

              {/* Contact & Social */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            >
                <h3 className={`text-lg font-bold mb-6 transition-colors duration-500 ${
                  isDarkMode ? 'text-white' : 'text-white'
                }`}>Get in Touch</h3>
                <div className="mb-6 space-y-3">
                <motion.div 
                  className="flex gap-3 items-start"
                  whileHover={{ x: 5 }}
                    transition={{ duration: 0.3 }}
                >
                    <MapPin size={16} className="flex-shrink-0 mt-1 text-blue-400" />
                    <p className={`text-sm font-semibold leading-relaxed transition-colors duration-500 ${
                      isDarkMode ? 'text-white' : 'text-white'
                    }`}>
                    C-116/2, Gali no 19, Molarband extension,<br />
                    Badarpur, New Delhi - 110044,<br />
                    Landmark: Kamal medical
                  </p>
                </motion.div>
                
                <motion.div 
                  className="flex gap-3 items-center"
                  whileHover={{ x: 5 }}
                    transition={{ duration: 0.3 }}
                >
                    <Mail size={16} className="flex-shrink-0 text-blue-400" />
                  <a 
                    href="mailto:support@skillsearthacademy.com" 
                      className={`text-sm font-semibold transition-colors duration-300 ${
                        isDarkMode 
                          ? 'text-white hover:text-blue-300' 
                          : 'text-white hover:text-blue-200'
                      }`}
                  >
                    support@skillsearthacademy.com
                  </a>
                </motion.div>
                
                <motion.div 
                  className="flex gap-3 items-center"
                  whileHover={{ x: 5 }}
                    transition={{ duration: 0.3 }}
                >
                    <Phone size={16} className="flex-shrink-0 text-blue-400" />
                    <div className="text-sm font-semibold">
                    <a href="tel:+917827153697" className={`transition-colors duration-300 block mb-1 ${
                      isDarkMode ? 'text-white hover:text-blue-300' : 'text-white hover:text-blue-200'
                    }`}>
                      +91 78271 53697
                    </a>
                    <a href="tel:+918586084775" className={`transition-colors duration-300 block ${
                      isDarkMode ? 'text-white hover:text-blue-300' : 'text-white hover:text-blue-200'
                    }`}>
                      +91 8586084775
                    </a>
                  </div>
                </motion.div>
              </div>

              {/* Social Links */}
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-3 rounded-lg transition-all duration-300 backdrop-blur-sm ${
                        isDarkMode 
                          ? 'text-white border bg-white/20 hover:bg-white/30 hover:text-blue-300 border-white/20 hover:border-white/40' 
                          : 'text-white border bg-white/30 hover:bg-white/40 hover:text-blue-200 border-white/30 hover:border-white/50'
                      }`}
                      whileHover={{ scale: 1.1, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                    >
                      <social.icon size={20} className="text-white" />
                    </motion.a>
                  ))}
              </div>
            </motion.div>
              </div>
            </div>
          </div>
        </div>

          {/* Bottom section */}
        <motion.div 
          className="border-t backdrop-blur-sm border-white/10 bg-black/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="px-4 py-3 mx-auto max-w-7xl sm:px-6 sm:py-4">
              {/* Mobile: Compact Bottom */}
              <div className="block sm:hidden">
                <div className="space-y-3 text-center">
                  <motion.p 
                    className={`text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-500 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                  >
                    © 2025 Skill Earth Academy. Made with 
                    <motion.span
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                    >
                      <Heart size={12} className="text-red-400" />
                    </motion.span>
                    in India
                  </motion.p>
                  <div className={`flex items-center justify-center gap-4 text-sm font-medium transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-300'
                  }`}>
                    {resourceLinks.map((link, index) => (
                      <motion.div
                        key={link.name}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                      >
                        <Link 
                          to={link.path} 
                          className={`hover:text-white transition-colors duration-300 font-semibold ${
                            isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Desktop: Original Bottom */}
              <div className="hidden sm:block">
              <div className="flex flex-col gap-4 justify-between items-center md:flex-row">
                <motion.p 
                  className={`text-sm font-medium flex items-center gap-2 transition-colors duration-500 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                >
                  © 2025 Skill Earth Academy. Made with 
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <Heart size={14} className="text-red-400" />
                  </motion.span>
                  in India
                </motion.p>
                <div className={`flex items-center gap-6 text-sm font-medium transition-colors duration-500 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-300'
                }`}>
                  {resourceLinks.map((link, index) => (
                    <motion.div
                      key={link.name}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                    >
                      <Link 
                        to={link.path} 
                        className={`hover:text-white transition-colors duration-300 font-semibold ${
                          isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        {link.name}
                </Link>
                    </motion.div>
                  ))}
                  </div>
                </div>
            </div>
          </div>
        </motion.div>
      </footer>

      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        title="Newsletter Subscription Successful!"
        message="Thank you for subscribing to Skills Earth Academy newsletter! You'll receive the latest insights, tips, and updates directly in your inbox. Check your email for confirmation."
      />
    </>
  );
};

export default Footer;
